import { Download, Trash2, Camera, Boxes, LogOut, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Header = ({ itemCount, onScan, onExport, onClear, onOpenSettings, aiOnline = true, user, onLogout }) => {
  return (
    <header
      data-testid="app-header"
      className="glass sticky top-0 z-40 border-b border-border"
    >
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl ring-1 ring-primary/30">
            <img src="/logo.png" alt="SmartScan" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-lg sm:text-xl font-extrabold tracking-tight text-slate-50 leading-none">
              SmartScan<span className="text-primary"> Inventory</span>
            </h1>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Boxes className="h-3 w-3" /> {itemCount} items tracked
              </span>
              <span className="hidden sm:inline-flex items-center gap-1.5">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${aiOnline ? "bg-accent pulse-dot" : "bg-muted-foreground"}`}
                />
                AI Engine {aiOnline ? "Online" : "Offline"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            data-testid="fields-settings-trigger"
            variant="ghost"
            size="icon"
            onClick={onOpenSettings}
            className="text-muted-foreground hover:text-primary hover:bg-primary/10"
            title="Fields & AI criteria"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
          <Button
            data-testid="clear-data-trigger"
            variant="ghost"
            size="icon"
            onClick={onClear}
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            title="Clear inventory"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button
            data-testid="export-csv-button"
            variant="outline"
            onClick={onExport}
            className="hidden sm:inline-flex border-border bg-secondary/40 hover:bg-secondary gap-2"
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button
            data-testid="camera-modal-trigger"
            onClick={onScan}
            className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-lg shadow-primary/20 transition-transform active:scale-95"
          >
            <Camera className="h-4 w-4" /> <span className="hidden xs:inline sm:inline">Scan Items</span>
          </Button>

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="ml-1 rounded-full ring-1 ring-border hover:ring-primary/50 transition-all">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-secondary text-slate-200 text-xs">
                      {(user.name || user.email || "U").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-100 truncate">{user.name || "Signed in"}</span>
                    <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={onLogout}
                  className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer gap-2"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};
